import { ChangeDetectorRef, Component, inject, ViewChild } from '@angular/core';
import { AutoUnsubscribe } from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialogRef } from '@angular/material/dialog';
import { ModalService } from 'src/app/shared/service/modal/modal.service';
import { GridEventOutput, GridModel } from 'src/app/shared/model/page-grid';
import { ApplyVccData, MatchedVccModel, MatchedVccResponse } from '../../model/match-vcc';
import { MatchingVccGridService } from '../../service/matching-vcc-grid.service';
import { DatePipe } from '@angular/common';
import { PageGridComponent } from 'src/app/shared/components/page-grid/page-grid.component';
import { ConfirmationDialogRevertMatchingVccComnponent } from '../confirmation-dialog-revert-matching-vcc/confirmation-dialog-revert-matching-vcc.component';
import { ConfirmationDialogMatchingVccComnponent } from '../confirmation-dialog-matching-vcc/confirmation-dialog-matching-vcc.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ComponentCanDeactivate } from 'src/infrastructure/guards/unsaved-changes.guard';
import { Merchant } from 'src/app/dashboard/model/merchant-guid';
import { MerchantsService } from 'src/infrastructure/merchants/service/merchants.service';
import { MatchingVccPaymentComponent } from '../matching-vcc-payment-detail/matching-vcc-payment-detail.component';
import { BusinessErrorsDialog } from '../business-errors-dialog/business-errors-dialog.component';

@AutoUnsubscribe()
@Component({
    selector: 'matching-vcc',
    templateUrl: './matching-vcc-v2.component.html',
    styleUrls: ['./matching-vcc-v2.component.scss'],
    standalone: false
})
export class MatchingVccV2Component implements ComponentCanDeactivate {
  @ViewChild('matchingVccGridRef') matchingVccGridRef?: PageGridComponent;
  @ViewChild('modalDetailRef') protected modalDetailRef !: ComponentType<unknown>;

  constructor(private cdr: ChangeDetectorRef) { }

  private itemsSave: ApplyVccData[] = [];
  private itemsNoSave: ApplyVccData[] = [];
  private currentItems: MatchedVccModel[] = [];
  private allowNavigate = true;

  private modalService = inject(ModalService);
  private datePipe = inject(DatePipe);
  private snackBar = inject(MatSnackBar);
  private matDialogRef?: MatDialogRef<ConfirmationDialogRevertMatchingVccComnponent>;
  private merchantService = inject(MerchantsService);
  private columnStyles = (item: MatchedVccModel) => {
    if (item?.isAmountMatches && !item?.userApproved && !item?.dateApproved && !item?.revert)
      return { 'background-color': '#D4FAE0' };
    if (item?.revert)
      return { 'background-color': '#A9DDF5' };
    if (item?.userApproved && item?.dateApproved)
      return { 'background-color': '' }
    return { 'background-color': '#FAD4D5' };
  }

  private linkStyles = (item: MatchedVccModel): Record<string, string> => {
    if (!item.businessErrors) return { cursor: 'text', 'text-decoration': 'none' };
    return { 'text-decoration': 'underline', color: '#ED6B6D' };
  }
  protected readonly matchingVccGrid: GridModel<MatchedVccResponse> = {
    gridDataTestId: 'matching-vcc',
    gridTitle: 'Connexpay Vcc Matching Report V2',
    replayButton: true,
    showActionColumn: true,
    singleColumnFilter: false,
    defaultSort: {
      active: 'id',
      direction: 'desc',
    },
    eventButton: {
      text: 'Apply Update',
    },
    columns: [
      {
        columnProperty: 'clientName',
        columnHeader: 'Merchant Name',
        sort: true,
        extraWidth: true,
        textLeft: true,
      },
      {
        columnProperty: 'orderNumber',
        columnHeader: 'Connexpay Trip Record Locator',
        sort: true,
        clickable: true,
        textLeft: true,
      },
      {
        columnProperty: 'cardLastFour',
        columnHeader: 'Card Last Four',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'merchantName',
        columnHeader: 'Vcc Provider Name',
        sort: true,
        extraWidth: true,
        textLeft: true,
      },
      {
        columnProperty: 'releasedDate',
        columnHeader: 'Vcc Provider Released Date',
        sort: true,
        processData: (date: string) => this.datePipe.transform(date, 'M/d/yyyy') ?? '',
      },
      {
        columnProperty: 'connexPayAccountingDailyDetailAmount',
        columnHeader: 'Vcc Provider Total',
        sort: true,
        clickable: true,
        linkEventFunction: (item: MatchedVccModel) =>
          `FinanceResources/MatchingVccPaymentDetail/${item.connexPayAccountingDailyDetailId}?FmsBookingId=${item?.fmsBookingId ?? 0}`,
      },
      {
        columnProperty: 'adxTripReference',
        columnHeader: 'Trip Record Locator',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'tramsInvoiceNumber',
        columnHeader: 'Trams Invoice Number',
        sort: true,
      },
      {
        columnProperty: 'tramsBookingNo',
        columnHeader: 'Trams Booking Number',
        sort: true,
      },
      {
        columnProperty: 'tramsDataSourceName',
        columnHeader: 'Data Source Name',
        sort: true,
      },
      {
        columnProperty: 'tramsBookingTotalFare',
        columnHeader: 'Trams Total',
        sort: true,
      },
      {
        columnProperty: 'tramsBookingCommAmt',
        columnHeader: 'Agent Commission',
        sort: true,
      },
      {
        columnProperty: 'cogs',
        columnHeader: 'Cogs',
        sort: true,
      },
      {
        columnProperty: 'varianceAmount',
        columnHeader: 'Suggested Adjustment Commission',
        sort: true,
        clickable: true,
        style: this.columnStyles,
        linkStyle: this.linkStyles,

      },

    ],
    filters: [
      {
        filterProperty: 'merchantGuid',
        filterDisplayName: 'Merchant name',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.merchantService.getMerchantsData(),
          dropdownValueProp: 'merchantGuid',
          dropdownDisplayWith: (item: Merchant) => item.fullName,
        }
      },
      {
        filterProperty: 'releasedDateFrom',
        lastFilterProperty: 'releasedDateTo',
        filterDisplayName: 'Vcc Provider Released Date',
        filterType: 'DATERANGE',
      },
      {
        filterProperty: 'tramsBookingStartDateFrom',
        lastFilterProperty: 'tramsBookingStartDateTo',
        filterDisplayName: 'Booking Start Date',
        filterType: 'DATERANGE',
      },
      {
        filterProperty: 'tramsBookingEndDateFrom',
        lastFilterProperty: 'tramsBookingEndDateTo',
        filterDisplayName: 'Booking End Date',
        filterType: 'DATERANGE',
      },
      {
        filterProperty: 'orderNumber',
        filterDisplayName: 'Connexpay Trip Record Locator',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'ConnexPayDailyDetailTramsBookingJoinModel',
          autocompleteColumnName: 'OrderNumber',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'tramsInvoiceNumber',
        filterDisplayName: 'Trams Invoice Number',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'ConnexPayDailyDetailTramsBookingJoinModel',
          autocompleteColumnName: 'TramsInvoiceNumber',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'merchantName',
        filterDisplayName: 'Vcc Provider Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Merchant',
          autocompleteColumnName: 'MerchantName',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'cardLastFour',
        filterDisplayName: 'Card Last Four',
        filterType: 'TEXT',
      },
      {
        filterProperty: 'isMapped',
        filterDisplayName: 'Mapped To Trams Data',
        filterType: 'RADIOBUTTON', 
        config: {
          radioButtonBoolean: true
        },
      },
      {
        filterProperty: 'isAmountMatches',
        filterDisplayName: `Matched Vcc`,
        filterType: 'RADIOBUTTON',
      },
      {
        filterProperty: 'isResolved',
        filterDisplayName: 'Resolved',
        filterType: 'RADIOBUTTON',
        config: {
          radioButtonBoolean: true,
        }
      },
    ],
    filtersInitial: { isMapped: true },
    gridGetService: inject(MatchingVccGridService)
  };

  canDeactivate(): boolean {
    return this.allowNavigate;
  }

  itemColorStyle(item: MatchedVccModel): Record<string, string> {
    if (item?.isAmountMatches && !item?.userApproved && !item?.dateApproved && !item?.revert) {
      return { 'background-color': '#D4FAE0' };
    }

    if (item?.revert) {
      return { 'background-color': '#A9DDF5' };
    }

    if (item?.userApproved && item?.dateApproved) {
      return { 'background-color': '' };
    }

    return { 'background-color': '#FAD4D5' };
  }

  applyRevert(element: MatchedVccModel): void {
    this.allowNavigate = false;

    if (element?.revert) {
      delete element.revert;
      this.setItemsSave();
      return;
    }

    this.modalService.open(ConfirmationDialogRevertMatchingVccComnponent)
      .afterClosed().subscribe((confirmation: boolean) => {
        if (confirmation) {
          element.revert = true;
          this.setItemsSave();
        }
      });
  }

  applyUpdate(): void {
    this.allowNavigate = false;

    if (this.itemsSave.length) {
      this.modalService.open(
        ConfirmationDialogMatchingVccComnponent,
        {
          data: { toSave: this.itemsSave, noSave: this.itemsNoSave },
        }
      ).afterClosed().subscribe((confirmation: boolean) => {
        if (confirmation) {
          this.matchingVccGridRef?.getItems();
          this.cdr.detectChanges();
        }
      });
    } else {
      this.snackBar.open('No items to save', 'Dismiss', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    }
  }

  setItemsSave(items?: any): void {
    if (items) {
      this.currentItems = items;
    }
    this.itemsSave = [];
    this.itemsNoSave = [];

    this.currentItems.forEach((item: any) => {
      if (item?.isEnabled && item?.isAmountMatches && !item?.userApproved && !item?.dateApproved && !item?.revert) {
        this.itemsSave.push({
          id: item?.connexPayAccountingDailyDetailId,
          fmsBookingId: item?.fmsBookingId,
          tramsBookingNo: item?.tramsBookingNo,
          merchantGuid: item?.merchantGuid,
          releasedDate: item?.releasedDate,
          adxTripReference: item?.adxTripReference,
          adjustedCommission: item?.varianceAmount,
          isResolved: item?.isResolved,
          isEnabled: item?.isEnabled,
        });
      } else {
        if (item?.isEnabled && item?.shouldResolve) {
          this.itemsSave.push({
            id: item?.connexPayAccountingDailyDetailId,
            fmsBookingId: item?.fmsBookingId,
            tramsBookingNo: item?.tramsBookingNo,
            merchantGuid: item?.merchantGuid,
            releasedDate: item?.releasedDate,
            adxTripReference: item?.adxTripReference,
            isResolved: item?.shouldResolve,
            isEnabled: item?.isEnabled
          });
        } else {
          this.itemsNoSave.push({
            id: item?.connexPayAccountingDailyDetailId,
            fmsBookingId: item?.fmsBookingId,
            tramsBookingNo: item?.tramsBookingNo,
            merchantGuid: item?.merchantGuid,
            releasedDate: item?.releasedDate,
            adxTripReference: item?.adxTripReference,
            adjustedCommission: item?.varianceAmount,
            isEnabled: item?.isEnabled
          });
        }
      }
    });
  }

  setResolve(item: MatchedVccModel): void {
    this.allowNavigate = false;

    if (item.shouldResolve) {
      delete item.shouldResolve;
    } else {
      item.shouldResolve = true;
    }

    this.setItemsSave();
  }

  openDetailDialog({ item, column }: GridEventOutput<MatchedVccModel>): void {
    if (column === 'orderNumber') {
      if (item.connexPayAccountingDailyDetailId) {
        this.matDialogRef = this.modalService.open(this.modalDetailRef, {
          width: '50vw',
          data: { id: item.connexPayAccountingDailyDetailId, fmsBookingId: item.fmsBookingId ?? 0 },
        });
      }
    }
    if (column === 'connexPayAccountingDailyDetailAmount') {
      this.matDialogRef = this.modalService.open(MatchingVccPaymentComponent, {
        width: '70dvw',
        data: item,
      });
    }
    if (column === 'varianceAmount' && item.businessErrors)
      this.openErrorsDialog(item.businessErrors);

  }
  handleToggleRecord() {
    this.setItemsSave();
  }

  closeDetailDialog(): void {
    this.matDialogRef?.close();
  }

  openErrorsDialog(errors: string) {
    this.modalService.open(BusinessErrorsDialog, { data: errors });
  }

}
